import { useEffect, useRef, useState } from "react";
import type { ExportJob } from "../lib/api.ts";
import { t, type Key } from "../lib/i18n.ts";
import type { EditorStore } from "../lib/store.ts";
import { Err, Modal, Spinner, msgOf, useApp } from "./common.tsx";

const mmss = (s: number): string => `${Math.floor(s / 60)}:${String(Math.max(0, Math.round(s % 60))).padStart(2, "0")}`;
const fmtSize = (b: number): string => b > 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`;
const fileName = (n?: string): string => (n && /\.mp4$/i.test(n) ? n : `${n || "modila"}.mp4`);

export function ExportModal({ store, onClose }: { store: EditorStore; onClose: () => void }) {
  const { api } = useApp();
  const [phase, setPhase] = useState("idle" as "idle" | "saving" | "running" | "done" | "failed" | "cancelled");
  const [job, setJob] = useState(null as ExportJob | null);
  const [err, setErr] = useState(null as string | null);
  const [elapsed, setElapsed] = useState(0);
  const [shared, setShared] = useState(false);
  const [quality, setQuality] = useState("standard" as "draft" | "standard" | "hd");
  const [loud, setLoud] = useState("off" as string);
  const alive = useRef(true), t0 = useRef(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (phase !== "running") return;
    const h = setInterval(() => setElapsed((Date.now() - t0.current) / 1000), 500);
    return () => clearInterval(h);
  }, [phase]);

  async function start() {
    setErr(null); setPhase("saving"); setJob(null); setElapsed(0);
    try {
      await store.saveNow();
      const sv = store.getState().save;
      if (sv !== "saved") { setErr(t(("save." + sv) as Key)); setPhase("idle"); return; }
      let j = await api.startExport(store.getState().project!.id, quality, loud === "off" ? undefined : Number(loud)); setJob(j); setPhase("running"); t0.current = Date.now();
      while (alive.current && (j.status === "queued" || j.status === "running")) {
        await new Promise((r) => setTimeout(r, 700));
        if (!alive.current) return;
        j = await api.getExport(j.id); setJob(j);
      }
      setPhase(j.status === "done" ? "done" : j.status === "cancelled" ? "cancelled" : "failed");
      if (j.status === "failed") setErr(j.error ?? t("ex.failed"));
    } catch (e) { setErr(msgOf(e)); setPhase("failed"); }
  }
  async function cancel() { if (job) { try { await api.cancelExport(job.id); } catch (e) { setErr(msgOf(e)); } } }
  async function share() {
    if (!job) return;
    try {
      const blob = await (await fetch(`/api/exports/${job.id}/file`)).blob();
      const f = new File([blob], fileName(job.name), { type: "video/mp4" });
      const nav = navigator as any;
      if (nav.canShare?.({ files: [f] })) { await nav.share({ files: [f], title: job.name }); setShared(true); }
    } catch { /* cancelled by the user or unsupported */ }
  }
  const p = store.getState().project!;
  const canShare = typeof navigator !== "undefined" && !!(navigator as any).canShare;
  const busy = phase === "saving" || phase === "running";
  const pct = Math.round((job?.progress ?? 0) * 100);
  const eta = job && job.progress > 0.04 ? elapsed * (1 - job.progress) / job.progress : null;
  const dur = (p.duration_ms / 1000).toFixed(1);
  const fmt = p.height > p.width ? "9:16" : p.height === p.width ? "1:1" : "16:9";
  const k = quality === "draft" ? 0.5 : quality === "standard" ? 0.667 : 1;
  const evenPx = (v: number) => (k >= 1 ? v : Math.max(2, Math.round(v * k / 2) * 2));
  return (
    <Modal title={t("ex.title")} onClose={() => { if (!busy) onClose(); }}>
      {(phase === "idle" || phase === "failed" || phase === "cancelled") && (
        <div className="stack">
          <div className="ex-q" role="radiogroup" aria-label={t("ex.quality")}>
            {(["draft", "standard", "hd"] as const).map((q) => (
              <button key={q} role="radio" aria-checked={quality === q} data-on={quality === q} onClick={() => setQuality(q)}>
                <b>{t(("ex.q_" + q) as Key)}</b><small>{t(("ex.q_" + q + "_d") as Key)}</small></button>))}
          </div>
          {p.audio.length > 0 && <label className="f" htmlFor="ex-loud">{t("ex.loud")}<select id="ex-loud" className="select" value={loud} onChange={(e: any) => setLoud(e.target.value)}>
            <option value="off">{t("ex.loud_off")}</option><option value="-14">-14 LUFS · {t("ex.loud_social")}</option><option value="-16">-16 LUFS · {t("ex.loud_video")}</option><option value="-18">-18 LUFS · {t("ex.loud_soft")}</option></select>
            <small className="muted">{t("ex.loud_hint")}</small></label>}
          <div className="ex-spec" aria-label={t("ex.spec")}>
            <span><b>MP4</b><small>H.264 + AAC</small></span><span><b>{evenPx(p.width)}×{evenPx(p.height)}</b><small>{fmt}</small></span>
            <span><b>{p.fps} fps</b><small>{dur} s</small></span>
          </div>
        </div>)}
      {phase === "idle" && <p className="muted" style={{ margin: 0 }}>{t("ex.note")}</p>}
      {phase === "saving" && <div><Spinner /> {t("ex.saving")}</div>}
      {phase === "running" && <div aria-live="polite" className="stack">
        <div className="ex-pct"><strong>{pct} %</strong><span className="muted">{job?.status === "queued" ? t("ex.queued") : t("ex.progress", { pct })}</span></div>
        <div className="bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>
        <small className="muted">{t("ex.time", { done: mmss(elapsed), left: eta === null ? "…" : mmss(eta) })}</small>
      </div>}
      {phase === "done" && job && <div className="stack ex-done">
        <video className="ex-video" src={`/api/exports/${job.id}/file`} controls playsInline preload="metadata" style={{ aspectRatio: `${p.width} / ${p.height}` }} />
        <div className="ex-file"><strong>{t("ex.done")}</strong><span className="muted mono">{fileName(job.name)}{job.size_bytes ? ` · ${fmtSize(job.size_bytes)}` : ""}</span></div>
        <a className="btn primary" href={`/api/exports/${job.id}/file`} download={fileName(job.name)}>{t("ex.download")}</a>
        <div className="ex-more">
          {canShare && <button className="btn" onClick={() => void share()}>{shared ? t("ex.shared") : t("ex.share")}</button>}
          {p.subtitles.cues.length > 0 && <a className="btn" href={`/api/projects/${encodeURIComponent(p.id)}/subtitles?format=srt`} download>{t("ex.srt")}</a>}
        </div>
      </div>}
      {phase === "cancelled" && <div>{t("ex.cancelled")}</div>}
      {phase === "failed" && <div><strong>{t("ex.failed")}</strong></div>}
      <Err>{err}</Err>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        {phase === "running" && <button className="btn" onClick={cancel}>{t("ex.cancel")}</button>}
        {(phase === "idle" || phase === "failed" || phase === "cancelled") && <button className="btn primary" onClick={() => void start()}>{t("ex.start")}</button>}
        {phase === "done" && <button className="btn" onClick={() => void start()}>{t("ex.again")}</button>}
        <button className="btn" disabled={busy} onClick={onClose}>{t("ex.close")}</button>
      </div>
    </Modal>
  );
}
